import { EntryMethod } from 'src/common/enumerations/entry-method';
import { ICancelLineItem } from 'src/common/interfaces/dtos/ICancelLineItem';
import { applyCartResponse } from 'src/home-page/business-logic/common/apply-cart-response';
import { lineItemCancel } from 'src/home-page/business-logic/service/line-item-cancel';
import { store } from '../../common/redux/configure-store';
import { cancelItemById } from '../../home-page/business-logic/cancel-line-or-item';
import { ItemCategory } from '../components/remove-items/remove-items-categories-modal';
import { InterventionTypes } from '../enumerations/intervention-types';
import { IInterventionInfo } from '../interfaces/IIntervention';
import {
  displayRemoveRejectedItemsModal,
  displayRemoveRejectedItemsWithCategoriesModal,
} from './display-remove-rejected-items';

export interface VoidItem {
  itemId: string;
  sequenceNumber: number;
  description?: string;
}

export enum VoidInterventionReason {
  VoidInterventionProhibit = 'VoidInterventionProhibit',
  VoidInterventionLockForSale = 'VoidInterventionLockForSale',
  VoidInterventionCustomerUnderAge = 'VoidInterventionCustomerUnderAge',
}

export const voidInterventionItemsCategorized = async (
  voidItemsMap: Map<VoidInterventionReason, VoidItem[]>,
  autoVoid: boolean = false,
): Promise<void> => {
  const voidItems: VoidItem[] = [];
  const voidItemCategories: ItemCategory[] = [];

  voidItemsMap.forEach((items: VoidItem[], key: VoidInterventionReason) => {
    let hasDescription = false;
    const voidItemCategory: ItemCategory = {
      category: key,
      itemDescriptions: items.map(voidItem => {
        if (voidItem.description && voidItem.description.length > 0) {
          hasDescription = true;
        }
        return voidItem.description ?? '';
      }),
    };
    hasDescription && voidItemCategories.push(voidItemCategory);
    voidItems.push(...items);
  });

  if (autoVoid) {
    await voidInterventionItems(voidItems, true);
  }

  await displayRemoveRejectedItemsWithCategoriesModal(voidItemCategories).then(async () => {
    if (!autoVoid) {
      await voidInterventionItems(voidItems, true);
    }
  });
};

/**
 * Displays list of items to void and voids them from transaction
 * @param itemIds list of items to void
 * @param autoVoid set to true if items should be voided before modal popup
 * @param cancelLine true if we should cancel line vs cancel item
 */
export const voidInterventionRejectedItems = async (
  itemIds: string[],
  autoVoid: boolean = true,
  cancelLine: boolean = false,
): Promise<void> => {
  const distictItemIds = [...new Set(itemIds)];
  const interventionGroups = store.getState().interventions.groups;
  /**
   * We need to retrieve the sequenceNumber and itemId of the items that are prohibited for sale
   * 1. Filter the restrictions with type "RestrictedByRule"
   * 2. From the filterred restriction, get the data of distinct prohibited items
   */

  const interventionsHasItemToDelete = interventionGroups.flatMap(x =>
    x.interventions.filter(
      intervention =>
        (intervention as IInterventionInfo).type === InterventionTypes.RestrictedByRule &&
        distictItemIds.includes((intervention as IInterventionInfo).itemId),
    ),
  );

  const itemDescriptions: string[] = [];
  const voidItems: VoidItem[] = interventionsHasItemToDelete.map(intervention => {
    const interventionInfo = intervention as IInterventionInfo;
    if (interventionInfo.itemLookup?.description) {
      itemDescriptions.push(interventionInfo.itemLookup?.description);
    }
    return {
      itemId: interventionInfo.itemId,
      sequenceNumber: interventionInfo.itemSequenceNumber,
    };
  });

  if (autoVoid) {
    await voidInterventionItems(voidItems, cancelLine);
  }

  await displayRemoveRejectedItemsModal(itemDescriptions).then(async () => {
    if (!autoVoid) {
      await voidInterventionItems(voidItems, cancelLine);
    }
  });
};

const voidInterventionItems = async (
  voidItems: VoidItem[],
  cancelLine: boolean = false,
): Promise<void> => {
  let cartResponse;

  for (const item of voidItems) {
    if (cancelLine) {
      const { transactionId } = store.getState().transaction;
      const inputData: ICancelLineItem = {
        sequenceNumber: item.sequenceNumber,
        entryMethod: EntryMethod.Tapped,
      };
      cartResponse = await lineItemCancel(inputData, transactionId);
    } else {
      await cancelItemById({
        sequenceNumber: item.sequenceNumber,
        itemId: item.itemId,
        reason: 'AutoVoid',
      });
    }
  }

  cancelLine && cartResponse && applyCartResponse(cartResponse);
};
